// `MeResourceDetail` (P3-20, `docs/design/P3-FE-04-player-resource-contract.md`
// §2-§5) — the student resource-detail projection served by
// `GET /v1/me/resources/:id`. Reuses the existing authoring per-type payload
// schemas in `./resource.ts` (`simulationResourcePayloadSchema` etc.) rather
// than restating them; only adds the read-projection deltas the contract
// doc calls for (SIMULATION's nested `guidance` block replacing P2-19's flat
// `whatToNotice`/`tasks` fields, and a required/clamped `sandbox`).
//
// FROZEN forward contract (contract doc I-5) the moment this ships — extend
// only additively afterward.
import { z } from 'zod';
import { ResourceType } from '../enums';
import { idSchema, positionSchema } from './primitives';
import {
  linkResourcePayloadSchema,
  noteResourcePayloadSchema,
  pdfResourcePayloadSchema,
  resourceTypeSchema,
  videoResourcePayloadSchema,
} from './resource';

// ─── §3.1 guidance block (SIMULATION only) ──────────────────────────────────

export const meResourceGuidanceTaskSchema = z.object({
  index: z.number().int().positive(),
  total: z.number().int().positive(),
  prompt: z.string(),
});
export type MeResourceGuidanceTask = z.infer<typeof meResourceGuidanceTaskSchema>;

export const meResourceGuidanceSchema = z.object({
  whatToNotice: z.string(),
  tasks: z.array(meResourceGuidanceTaskSchema),
});
export type MeResourceGuidance = z.infer<typeof meResourceGuidanceSchema>;

// ─── §3 per-type payload projections ────────────────────────────────────────
// VIDEO/PDF/LINK/NOTE are unchanged from the authoring shape (contract doc
// §3: "VIDEO/other payload shapes unchanged") — reused verbatim, not forked.

export const meNoteResourcePayloadSchema = noteResourcePayloadSchema;
export type MeNoteResourcePayload = z.infer<typeof meNoteResourcePayloadSchema>;

export const meVideoResourcePayloadSchema = videoResourcePayloadSchema;
export type MeVideoResourcePayload = z.infer<typeof meVideoResourcePayloadSchema>;

export const mePdfResourcePayloadSchema = pdfResourcePayloadSchema;
export type MePdfResourcePayload = z.infer<typeof mePdfResourcePayloadSchema>;

export const meLinkResourcePayloadSchema = linkResourcePayloadSchema;
export type MeLinkResourcePayload = z.infer<typeof meLinkResourcePayloadSchema>;

/** §3.2/§3.4 — `sandbox` is ALWAYS present (server-clamped, never absent) and
 * `guidance` is ALWAYS present, explicit `null` when the sim carries none
 * (contract doc §3.1: "explicit null, not an absent key"). `mode: 'bundle'`
 * responses carry `bundleAssetId` only, never a signed URL (I-4) — the
 * schema itself doesn't need to special-case that; the service layer simply
 * never populates a URL-looking field for bundle mode. */
export const meSimulationResourcePayloadSchema = z.object({
  mode: z.enum(['iframe', 'bundle', 'r2-bundle']),
  url: z.string().trim().url().optional(),
  bundleAssetId: idSchema.optional(),
  aspectRatio: z.string().trim().min(1).max(16).optional(),
  sandbox: z.array(z.string()),
  guidance: meResourceGuidanceSchema.nullable(),
});
export type MeSimulationResourcePayload = z.infer<typeof meSimulationResourcePayloadSchema>;

// ─── §2 provenance (mirrors P3-17's `CatalogueCard.provenance`, plus `tab`) ─

export const meResourceProvenanceSchema = z.object({
  level: z.object({ id: idSchema, name: z.string() }),
  subject: z.object({ id: idSchema, title: z.string() }),
  section: z.object({ id: idSchema, title: z.string() }),
  tab: z.object({ id: idSchema, kind: z.string(), label: z.string() }),
});
export type MeResourceProvenance = z.infer<typeof meResourceProvenanceSchema>;

// ─── §4 bounded, inline sibling navigation ──────────────────────────────────

export const meResourceSiblingStubSchema = z.object({
  resourceId: idSchema,
  title: z.string(),
  type: resourceTypeSchema,
});
export type MeResourceSiblingStub = z.infer<typeof meResourceSiblingStubSchema>;

export const meResourceSiblingsSchema = z.object({
  position: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  prev: meResourceSiblingStubSchema.nullable(),
  next: meResourceSiblingStubSchema.nullable(),
});
export type MeResourceSiblings = z.infer<typeof meResourceSiblingsSchema>;

// ─── §2 the discriminated `MeResourceDetail` response ───────────────────────

const meResourceDetailBaseFields = {
  id: idSchema,
  subtype: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  durationSec: z.number().int().positive().nullable(),
  isFreePreview: z.boolean(),
  position: positionSchema,
  provenance: meResourceProvenanceSchema,
  siblings: meResourceSiblingsSchema,
  updatedAt: z.string(),
};

export const meResourceDetailSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(ResourceType.NOTE),
    ...meResourceDetailBaseFields,
    payload: meNoteResourcePayloadSchema,
  }),
  z.object({
    type: z.literal(ResourceType.VIDEO),
    ...meResourceDetailBaseFields,
    payload: meVideoResourcePayloadSchema,
  }),
  z.object({
    type: z.literal(ResourceType.SIMULATION),
    ...meResourceDetailBaseFields,
    payload: meSimulationResourcePayloadSchema,
  }),
  z.object({
    type: z.literal(ResourceType.PDF),
    ...meResourceDetailBaseFields,
    payload: mePdfResourcePayloadSchema,
  }),
  z.object({
    type: z.literal(ResourceType.LINK),
    ...meResourceDetailBaseFields,
    payload: meLinkResourcePayloadSchema,
  }),
]);
export type MeResourceDetail = z.infer<typeof meResourceDetailSchema>;

// ─── `POST /v1/me/resources/:id/access-url` response (P3-22 §3.3) ───────────
// Additive to this file — does NOT alter `meResourceDetailSchema` or any
// payload schema above. A discriminated union because the same student write
// now serves two distinct playback surfaces: a signed asset GET (PDF/bundle
// download) or a signed r2-hls manifest (VIDEO). The ≤5-min-TTL manifest
// deliberately never lives inside the 60s-stale-time cached `MeResourceDetail`
// (I-4) — it is minted fresh on each call to this endpoint instead.

export const meResourceAccessUrlResponseSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('asset'),
    url: z.string(),
    expiresIn: z.number().int().positive(),
  }),
  z.object({
    kind: z.literal('hls'),
    manifest: z.string(),
    expiresIn: z.number().int().positive(),
  }),
]);
export type MeResourceAccessUrlResponse = z.infer<typeof meResourceAccessUrlResponseSchema>;
